import { Trash } from "@phosphor-icons/react";

import RestrictedComponent from "common/components/RestrictedComponent";
import { SplitButton } from "common/components/SplitButton";
import { Tag } from "common/components/ui/Tag";
import type { RentedSubscription } from "common/types";

import NonRenewingTag from "../NonRenewingTag";
import RemoveChangeButton from "../RemoveChangeButton";
import SubscriptionBillingPanel from "../SubscriptionBillingPanel";
import Accounts from "./Accounts";
import ScheduledChangeTag from "./ScheduledChangeTag";

interface AvatarOrderProps {
  orderNumber: number;
  subscription: RentedSubscription;
  onManageClick: () => void;
  onCancelSubscriptionClick: () => void;
}

export default function AvatarOrder({
  orderNumber,
  subscription,
  onManageClick,
  onCancelSubscriptionClick,
}: AvatarOrderProps) {
  const { status, item_price: itemPrice, has_changes, id } = subscription;

  const isScheduledCancellation = status === "non_renewing";
  const removeChangeType = isScheduledCancellation ? "cancellation" : "change";
  const showEndState = isScheduledCancellation || has_changes;

  const { rented_count: rentedCount } = subscription;

  return (
    <section className="w-full grid lg:grid-cols-2 gap-x-2 p-1.5 bg-whiteGray rounded-5xl border border-black-200">
      <div className="flex flex-col gap-6 p-5 justify-between">
        <div className="flex flex-row items-center justify-between gap-3">
          <span className="text-lg-bold">Avatar Order #{orderNumber}</span>

          <div className="flex flex-row items-center gap-2">
            {itemPrice.period === 6 && (
              <Tag variant="secondary">6 months deal</Tag>
            )}

            {status === "non_renewing" && <NonRenewingTag type="avatar" />}

            {has_changes && status !== "non_renewing" && (
              <ScheduledChangeTag
                subscriptionId={id}
                rentedCount={rentedCount}
                type="avatar"
              />
            )}
          </div>
        </div>
        <Accounts subscriptionId={id} rentedCount={rentedCount} />
        <RestrictedComponent visibleFor={["billing_full"]}>
          <div className="ml-auto">
            {showEndState ? (
              <RemoveChangeButton
                subscription={subscription}
                type={removeChangeType}
                isInitiallyDisabled={false}
              />
            ) : (
              <SplitButton
                size="sm"
                onClick={onManageClick}
                dropdownMenuItems={[
                  {
                    label: "Cancel order",
                    icon: Trash,
                    onSelect: onCancelSubscriptionClick,
                    variant: "danger",
                  },
                ]}
              >
                Manage
              </SplitButton>
            )}
          </div>
        </RestrictedComponent>
      </div>

      <SubscriptionBillingPanel subscription={subscription} />
    </section>
  );
}
